import React, {useEffect, useMemo, useState} from "react";
import styles from "./AnalyticsCharts.module.css";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {
    ConversationChannel,
    TranscriptAnalyticsCountByChannel,
    TranscriptAnalyticsCountByLabel,
    TranscriptAnalyticsDailyTrendPoint,
    TranscriptAnalyticsNewVsReturningUsers,
    TranscriptAnalyticsOverview
} from "../../../../../../api/generated";
import {HelpTooltip} from "../HelpTooltip/HelpTooltip";

const CHART_COLORS = ["#D1A852", "#27203D", "#5B6C7D", "#00ACFF", "#16A34A", "#C2410C", "#475569", "#4D545B"];
const numberFormatter = new Intl.NumberFormat("en-GB");

const CHART_TOOLTIPS = {
    dailyTrend: "Shows daily conversations and communicated users using your browser timezone day boundaries.",
    newVsReturning: "Compares active new users vs returning users for selected filters.",
    messageType: "Distribution of assistant message types.",
    toolUsage: "Top tools by call count for selected filters.",
    channelDistribution: "Conversation count split by channel.",
    tokenUsage: "Input/output token totals and per-user/per-conversation averages.",
};

interface GroupMetric {
    label: string;
    value: string;
    tooltip: string;
}

const channelLabelMap: Record<ConversationChannel, string> = {
    [ConversationChannel.Widget]: "Widget",
    [ConversationChannel.SuperchatWhatsApp]: "Superchat WhatsApp",
    [ConversationChannel.SuperchatEmail]: "Superchat Email",
    [ConversationChannel.SuperchatSms]: "Superchat SMS",
    [ConversationChannel.SuperchatTelegram]: "Superchat Telegram",
    [ConversationChannel.SuperchatInstagram]: "Superchat Instagram",
    [ConversationChannel.SuperchatFacebook]: "Superchat Facebook",
    [ConversationChannel.ContactCenterWhatsApp]: "Contact Center WhatsApp",
    [ConversationChannel.ContactCenterEmail]: "Contact Center Email",
    [ConversationChannel.ContactCenterSms]: "Contact Center SMS",
    [ConversationChannel.ConfidentAi]: "Confident AI",
};

interface AnalyticsChartsProps {
    overview?: TranscriptAnalyticsOverview;
    dailyTrend: TranscriptAnalyticsDailyTrendPoint[];
    newVsReturning?: TranscriptAnalyticsNewVsReturningUsers;
    messageTypeBreakdown: TranscriptAnalyticsCountByLabel[];
    toolUsageBreakdown: TranscriptAnalyticsCountByLabel[];
    channelBreakdown: TranscriptAnalyticsCountByChannel[];
}

export const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({
    overview,
    dailyTrend,
    newVsReturning,
    messageTypeBreakdown,
    toolUsageBreakdown,
    channelBreakdown,
}) => {
    const [hiddenDonutSections, setHiddenDonutSections] = useState<string[]>([]);
    const [hiddenMessageTypeSections, setHiddenMessageTypeSections] = useState<string[]>([]);

    const newVsReturningData = useMemo(() => ([
        {name: "New Users", value: newVsReturning?.newUsers ?? 0},
        {name: "Returning Users", value: newVsReturning?.returningUsers ?? 0}
    ]), [newVsReturning]);

    const messageTypeData = useMemo(
        () => mapLabelBreakdown(messageTypeBreakdown),
        [messageTypeBreakdown]
    );

    const toolUsageData = useMemo(
        () => mapLabelBreakdown(toolUsageBreakdown)
            .sort((first, second) => second.value - first.value)
            .slice(0, 10),
        [toolUsageBreakdown]
    );

    const channelData = useMemo(
        () => mapChannelBreakdown(channelBreakdown),
        [channelBreakdown]
    );

    const donutColorByName = useMemo(
        () => Object.fromEntries(newVsReturningData.map((item, idx) => [item.name, CHART_COLORS[idx % CHART_COLORS.length]])),
        [newVsReturningData]
    );

    const messageTypeColorByName = useMemo(
        () => Object.fromEntries(messageTypeData.map((item, idx) => [item.name, CHART_COLORS[idx % CHART_COLORS.length]])),
        [messageTypeData]
    );

    const visibleDonutData = useMemo(
        () => newVsReturningData.filter(item => !hiddenDonutSections.includes(item.name)),
        [newVsReturningData, hiddenDonutSections]
    );

    const visibleMessageTypeData = useMemo(
        () => messageTypeData.filter(item => !hiddenMessageTypeSections.includes(item.name)),
        [messageTypeData, hiddenMessageTypeSections]
    );

    const hasDonutData = newVsReturningData.some(item => item.value > 0);
    const hasMessageTypeData = messageTypeData.length > 0;

    useEffect(() => {
        setHiddenDonutSections(prev => prev.filter(name => newVsReturningData.some(item => item.name === name)));
    }, [newVsReturningData]);

    useEffect(() => {
        setHiddenMessageTypeSections(prev => prev.filter(name => messageTypeData.some(item => item.name === name)));
    }, [messageTypeData]);

    const toggleDonutSection = (name: string) => {
        setHiddenDonutSections(prev => toggleListValue(prev, name));
    };

    const toggleMessageTypeSection = (name: string) => {
        setHiddenMessageTypeSections(prev => toggleListValue(prev, name));
    };

    const formatMetric = (value?: number): string => numberFormatter.format(value ?? 0);
    const formatPercent = (value?: number): string => `${((value ?? 0) * 100).toFixed(1)}%`;
    const formatDuration = (seconds?: number): string => `${(seconds ?? 0).toFixed(2)}s`;

    const dailyTrendPrimaryMetrics: GroupMetric[] = [
        {
            label: "Communicated Users",
            value: formatMetric(overview?.communicatedUsers),
            tooltip: "Distinct users with at least one manual user message."
        },
        {
            label: "Conversations",
            value: formatMetric(overview?.conversations),
            tooltip: "Number of conversations matching current filters."
        },
        {
            label: "New Users",
            value: formatMetric(overview?.newUsers),
            tooltip: "Active users created during the selected date range."
        },
        {
            label: "Returning Users",
            value: formatMetric(overview?.returningUsers),
            tooltip: "Active users created before the selected start date."
        },
        {
            label: "Avg Messages / Conversation",
            value: `${(overview?.averageMessagesPerConversation ?? 0).toFixed(2)}`,
            tooltip: "Total messages divided by total conversations."
        },
    ];

    const dailyTrendQualityMetrics: GroupMetric[] = [
        {
            label: "Avg Response Time",
            value: formatDuration(overview?.averageResponseTimeSeconds),
            tooltip: "Average assistant response time in seconds."
        },
        {
            label: "Success Rate",
            value: formatPercent(overview?.successRate),
            tooltip: "(Assistant messages - failed responses) / assistant messages."
        },
        {
            label: "Failed Responses",
            value: formatMetric(overview?.failedResponses),
            tooltip: "Assistant messages marked as unsuccessful responses."
        },
        {
            label: "Assistant Messages",
            value: formatMetric(overview?.assistantMessages),
            tooltip: "Total number of assistant messages."
        },
    ];

    const tokenMetrics: GroupMetric[] = [
        {
            label: "Input Tokens Used",
            value: formatMetric(overview?.totalInputTokensUsed),
            tooltip: "Sum of total_input_tokens for assistant responses."
        },
        {
            label: "Output Tokens Used",
            value: formatMetric(overview?.totalOutputTokensUsed),
            tooltip: "Sum of total_output_tokens for assistant responses."
        },
        {
            label: "Avg Input / Conversation",
            value: `${(overview?.averageInputTokensPerConversation ?? 0).toFixed(2)}`,
            tooltip: "Input tokens divided by total conversations."
        },
        {
            label: "Avg Output / Conversation",
            value: `${(overview?.averageOutputTokensPerConversation ?? 0).toFixed(2)}`,
            tooltip: "Output tokens divided by total conversations."
        },
        {
            label: "Avg Input / User",
            value: `${(overview?.averageInputTokensPerUser ?? 0).toFixed(2)}`,
            tooltip: "Input tokens divided by communicated users."
        },
        {
            label: "Avg Output / User",
            value: `${(overview?.averageOutputTokensPerUser ?? 0).toFixed(2)}`,
            tooltip: "Output tokens divided by communicated users."
        },
    ];

    return <div className={styles.chartGrid}>
        <div className={`${styles.chartCard} ${styles.dailyTrendCard}`}>
            <div className={styles.titleWithTooltip}>
                <div className={styles.chartCardHeader}>Daily Trend</div>
                <HelpTooltip text={CHART_TOOLTIPS.dailyTrend}/>
            </div>
            <div className={styles.dailyStatsGrid}>
                <div className={styles.dailyStatsRow}>
                    {dailyTrendPrimaryMetrics.map((metric, idx) => (
                        <MetricValueCard
                            key={`daily-primary-metric-${idx}`}
                            label={metric.label}
                            value={metric.value}
                            tooltip={metric.tooltip}
                            compact
                        />
                    ))}
                </div>
                <div className={`${styles.dailyStatsRow} ${styles.dailyStatsRowSecondary}`}>
                    {dailyTrendQualityMetrics.map((metric, idx) => (
                        <MetricValueCard
                            key={`daily-quality-metric-${idx}`}
                            label={metric.label}
                            value={metric.value}
                            tooltip={metric.tooltip}
                            compact
                        />
                    ))}
                </div>
            </div>
            {dailyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis dataKey="date"/>
                        <YAxis allowDecimals={false}/>
                        <Tooltip/>
                        <Legend/>
                        <Line type="monotone" dataKey="conversations" stroke="#D1A852" strokeWidth={2} name="Conversations"/>
                        <Line type="monotone" dataKey="communicatedUsers" stroke="#27203D" strokeWidth={2}
                              name="Communicated Users"/>
                    </LineChart>
                </ResponsiveContainer>
            ) : <ChartEmptyState message={"No trend data for selected filters"}/>}
        </div>

        <div className={`${styles.chartCard} ${styles.toolUsageCard}`}>
            <div className={styles.titleWithTooltip}>
                <div className={styles.chartCardHeader}>Tool Usage</div>
                <HelpTooltip text={CHART_TOOLTIPS.toolUsage}/>
            </div>
            <div className={styles.toolCallCardWrapper}>
                <MetricValueCard
                    label={"Total usage count"}
                    value={formatMetric(overview?.totalToolCalls)}
                />
            </div>
            {toolUsageData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={toolUsageData} margin={{top: 8, right: 12, left: 0, bottom: 56}}>
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis
                            dataKey="name"
                            interval={0}
                            angle={-22}
                            textAnchor="end"
                            height={72}
                            tickFormatter={truncateAxisTick}
                        />
                        <YAxis allowDecimals={false}/>
                        <Tooltip formatter={(value: number) => numberFormatter.format(value)}/>
                        <Bar dataKey="value" fill="#D1A852" radius={[6, 6, 0, 0]} maxBarSize={58}/>
                    </BarChart>
                </ResponsiveContainer>
            ) : <ChartEmptyState message={"No tool usage for selected filters"}/>}
        </div>

        <div className={styles.chartCard}>
            <div className={styles.titleWithTooltip}>
                <div className={styles.chartCardHeader}>New vs Returning Users</div>
                <HelpTooltip text={CHART_TOOLTIPS.newVsReturning}/>
            </div>
            {hasDonutData ? (
                <>
                    {visibleDonutData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={visibleDonutData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={95}
                                    dataKey="value"
                                    nameKey="name"
                                    label
                                >
                                    {visibleDonutData.map((entry, index) => (
                                        <Cell key={`new-vs-returning-${index}`}
                                              fill={donutColorByName[entry.name] || CHART_COLORS[index % CHART_COLORS.length]}/>
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => numberFormatter.format(value)}/>
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <ChartEmptyState message={"All sections are hidden. Click legend items to show them again."}/>
                    )}
                    <InteractiveLegend
                        items={newVsReturningData}
                        hiddenList={hiddenDonutSections}
                        colorByName={donutColorByName}
                        onToggle={toggleDonutSection}
                    />
                </>
            ) : <ChartEmptyState message={"No user split data for selected filters"}/>}
        </div>

        <div className={styles.chartCard}>
            <div className={styles.titleWithTooltip}>
                <div className={styles.chartCardHeader}>Message Type Distribution</div>
                <HelpTooltip text={CHART_TOOLTIPS.messageType}/>
            </div>
            {hasMessageTypeData ? (
                <>
                    {visibleMessageTypeData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie data={visibleMessageTypeData} dataKey="value" nameKey="name" outerRadius={100} label>
                                    {visibleMessageTypeData.map((entry, index) => (
                                        <Cell key={`message-type-${index}`}
                                              fill={messageTypeColorByName[entry.name] || CHART_COLORS[index % CHART_COLORS.length]}/>
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value: number) => numberFormatter.format(value)}/>
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <ChartEmptyState message={"All sections are hidden. Click legend items to show them again."}/>
                    )}
                    <InteractiveLegend
                        items={messageTypeData}
                        hiddenList={hiddenMessageTypeSections}
                        colorByName={messageTypeColorByName}
                        onToggle={toggleMessageTypeSection}
                    />
                </>
            ) : <ChartEmptyState message={"No assistant message types for selected filters"}/>}
        </div>

        <div className={`${styles.chartCard} ${styles.summaryCard}`}>
            <div className={styles.titleWithTooltip}>
                <div className={styles.chartCardHeader}>Token Usage</div>
                <HelpTooltip text={CHART_TOOLTIPS.tokenUsage}/>
            </div>
            <div className={styles.summaryMetricGrid}>
                {tokenMetrics.map((metric, idx) => (
                    <MetricValueCard
                        key={`token-usage-${idx}`}
                        label={metric.label}
                        value={metric.value}
                        tooltip={metric.tooltip}
                    />
                ))}
            </div>
        </div>

        <div className={styles.chartCard}>
            <div className={styles.titleWithTooltip}>
                <div className={styles.chartCardHeader}>Channel Distribution</div>
                <HelpTooltip text={CHART_TOOLTIPS.channelDistribution}/>
            </div>
            {channelData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={channelData}>
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis dataKey="name"/>
                        <YAxis allowDecimals={false}/>
                        <Tooltip formatter={(value: number) => numberFormatter.format(value)}/>
                        <Bar dataKey="value" fill="#27203D"/>
                    </BarChart>
                </ResponsiveContainer>
            ) : <ChartEmptyState message={"No channel distribution for selected filters"}/>}
        </div>
    </div>;
};

const ChartEmptyState: React.FC<{ message: string }> = ({message}) => (
    <div className={styles.emptyState}>{message}</div>
);

const InteractiveLegend: React.FC<{
    items: Array<{ name: string, value: number }>,
    hiddenList: string[],
    colorByName: Record<string, string>,
    onToggle: (name: string) => void,
}> = ({items, hiddenList, colorByName, onToggle}) => (
    <div className={styles.chartLegend}>
        {items.map((item, idx) => {
            const isHidden = hiddenList.includes(item.name);
            return (
                <button
                    type="button"
                    key={`legend-item-${item.name}-${idx}`}
                    className={`${styles.chartLegendItem} ${isHidden ? styles.chartLegendItemHidden : ""}`}
                    onClick={() => onToggle(item.name)}
                >
                    <span className={styles.chartLegendDot}
                          style={{backgroundColor: colorByName[item.name] || CHART_COLORS[idx % CHART_COLORS.length]}}/>
                    <span className={styles.chartLegendText}>{item.name}</span>
                </button>
            );
        })}
    </div>
);

const MetricValueCard: React.FC<{ label: string, value: string, tooltip?: string, compact?: boolean }> = ({label, value, tooltip, compact = false}) => (
    <div className={`${styles.metricCard} ${compact ? styles.metricCardCompact : styles.metricCardDefault}`}>
        <span className={styles.metricLabel}>{label}</span>
        <span className={styles.metricValueRow}>
            <span className={styles.metricValue}>{value}</span>
            {tooltip && <HelpTooltip text={tooltip}/>}
        </span>
    </div>
);

const mapLabelBreakdown = (breakdown: TranscriptAnalyticsCountByLabel[]): Array<{ name: string, value: number }> => {
    return breakdown.map(item => ({
        name: normalizeLabel(item.label),
        value: item.count ?? 0
    }));
};

const mapChannelBreakdown = (breakdown: TranscriptAnalyticsCountByChannel[]): Array<{ name: string, value: number }> => {
    return breakdown.map(item => ({
        name: item.conversationChannel ? channelLabelMap[item.conversationChannel] : "Unknown",
        value: item.count ?? 0
    }));
};

const normalizeLabel = (label?: string): string => {
    if (!label) {
        return "Unknown";
    }
    return label
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase());
};

const toggleListValue = (list: string[], value: string): string[] => {
    if (list.includes(value)) {
        return list.filter(item => item !== value);
    }
    return [...list, value];
};

const truncateAxisTick = (label?: string): string => {
    if (!label) {
        return "";
    }
    if (label.length <= 18) {
        return label;
    }
    return `${label.slice(0, 15)}...`;
};
